import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { useToast } from "@/hooks/use-toast";
import type { Product } from "./useProducts";
import { useEffect } from "react";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { useTranslation } from "react-i18next";

// Cache for guest session client to avoid creating multiple instances
let guestClientCache: { client: SupabaseClient | null; token: string | null } = {
  client: null,
  token: null,
};

export interface GuestInfo {
  id: string;
  cart_item_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  document_number: string;
  document_type: 'passport' | 'dni';
  gender: 'M' | 'F';
  created_at: string;
  updated_at: string;
}

export interface CartItem {
  id: string;
  cart_id: string;
  product_id: string;
  quantity: number;
  price_eur: number;
  booking_details: any;
  guests?: GuestInfo[];
  product?: Product;
  validation_status?: 'pending' | 'validating' | 'validated' | 'changes_found' | 'not_available';
  search_token?: string;
  validation_error?: string;
  validated_at?: string;
}

// Create a guest session via edge function with cryptographic validation
const createGuestSession = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('create-guest-session');
    
    if (error) {
      console.error('Error creating guest session:', error);
      throw error;
    }
    
    // Store session_id and session_token
    localStorage.setItem('guest_session_id', data.session_id);
    localStorage.setItem('guest_session_token', data.session_token);
    
    return {
      session_id: data.session_id,
      session_token: data.session_token,
    };
  } catch (error) {
    console.error('Failed to create guest session:', error);
    throw error;
  }
};

// Get existing guest session from localStorage (only if not expired)
const getGuestSession = async () => {
  const session_id = localStorage.getItem('guest_session_id');
  const session_token = localStorage.getItem('guest_session_token');
  
  if (!session_id || !session_token) {
    return null;
  }
  
  // Verify session is still valid and not expired using secure client
  try {
    const client = getSupabaseClient(session_token);

    const { data, error } = await client
      .from('guest_sessions')
      .select('expires_at')
      .eq('id', session_id)
      .eq('session_token', session_token)
      .maybeSingle();
    
    if (error || !data) {
      // Session doesn't exist, clear localStorage
      localStorage.removeItem('guest_session_id');
      localStorage.removeItem('guest_session_token');
      return null;
    }
    
    // Check if expired
    if (new Date(data.expires_at) <= new Date()) {
      // Session expired, clear localStorage
      localStorage.removeItem('guest_session_id');
      localStorage.removeItem('guest_session_token');
      return null;
    }
    
    return { session_id, session_token };
  } catch (error) {
    console.error('Error checking guest session:', error);
    return null;
  }
};

// Get or create Supabase client with session token in headers (cached to avoid multiple instances)
const getSupabaseClient = (sessionToken?: string) => {
  if (!sessionToken) {
    // Return default client for authenticated users
    return supabase;
  }
  
  // Check if we have a cached client for this token
  if (guestClientCache.client && guestClientCache.token === sessionToken) {
    return guestClientCache.client;
  }
  
  // Create a new client with custom headers for guest sessions
  // Use a unique storage key to avoid conflicts with the main auth client
  const storageKey = `sb-guest-${sessionToken.substring(0, 8)}`;
  
  const client = createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storageKey: storageKey, // Unique key prevents GoTrueClient conflicts
      },
      global: {
        headers: {
          'x-session-token': sessionToken,
        },
      },
    }
  );
  
  // Cache the client
  guestClientCache = { client, token: sessionToken };
  
  return client;
};

export const useCart = () => {
  const { toast } = useToast();
  const { t, i18n: i18nInstance } = useTranslation();
  const queryClient = useQueryClient();

  // Migrate guest cart to user cart on login
  useEffect(() => {
    const migrateGuestCart = async (userId: string) => {
      const guestSession = await getGuestSession();
      
      if (guestSession) {
        try {
          // Find guest cart using guest_session_id
          const { data: guestCart } = await supabase
            .from("cart")
            .select("id")
            .eq("guest_session_id", guestSession.session_id)
            .maybeSingle();

          if (guestCart) {
            // Check if user already has a cart
            const { data: userCart } = await supabase
              .from("cart")
              .select("id")
              .eq("user_id", userId)
              .maybeSingle();

            if (userCart) {
              // Merge guest cart items into user cart
              const { data: guestItems } = await supabase
                .from("cart_items")
                .select("*")
                .eq("cart_id", guestCart.id);

              if (guestItems && guestItems.length > 0) {
                // Update cart_id for all guest items to user cart
                await supabase
                  .from("cart_items")
                  .update({ cart_id: userCart.id })
                  .eq("cart_id", guestCart.id);
              }

              // Delete guest cart
              await supabase
                .from("cart")
                .delete()
                .eq("id", guestCart.id);
            } else {
              // Transfer ownership of guest cart to user
              await supabase
                .from("cart")
                .update({ 
                  user_id: userId,
                  guest_session_id: null 
                })
                .eq("id", guestCart.id);
            }

            // Clear guest session from localStorage
            localStorage.removeItem('guest_session_id');
            localStorage.removeItem('guest_session_token');

            // Refresh cart data
            queryClient.invalidateQueries({ queryKey: ["cart"] });
            queryClient.invalidateQueries({ queryKey: ["cart-items"] });

            toast({
              title: t('cart.cartMigrated'),
              description: t('cart.cartMigratedDescription'),
            });
          }
        } catch (error) {
          console.error('Error migrating guest cart:', error);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only synchronous operations here to avoid deadlock
        if (event === 'SIGNED_IN' && session?.user) {
          const userId = session.user.id;
          // Defer async operations with setTimeout
          setTimeout(() => {
            migrateGuestCart(userId);
          }, 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient, toast, t]);

  const { data: cart, isLoading: isLoadingCart } = useQuery({
    queryKey: ["cart"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      // For logged-in users
      if (user) {
        // Get the most recent cart with items, or fallback to most recent cart
        const { data: cartsWithItems } = await supabase
          .from("cart")
          .select(`
            *,
            cart_items(id)
          `)
          .eq("user_id", user.id)
          .order('updated_at', { ascending: false });
        
        // Prefer cart with items, otherwise use most recent
        const cartWithItems = cartsWithItems?.find(c => c.cart_items && c.cart_items.length > 0);
        const existingCart = cartWithItems || cartsWithItems?.[0];

        if (existingCart) {
          // Remove cart_items from the return (it was only for checking)
          const { cart_items, ...cartData } = existingCart;
          return cartData;
        }

        const { data: newCart, error } = await supabase
          .from("cart")
          .insert({ user_id: user.id })
          .select()
          .single();

        if (error) throw error;
        return newCart;
      }
      
      // For guest users - get or create secure session
      let guestSession = await getGuestSession();
      
      if (!guestSession) {
        // Create new secure session via edge function
        guestSession = await createGuestSession();
      }
      
      // Use client with session token in headers
      const client = getSupabaseClient(guestSession.session_token);
      
      const { data: existingCart } = await client
        .from("cart")
        .select()
        .eq("guest_session_id", guestSession.session_id)
        .maybeSingle();

      if (existingCart) return existingCart;

      const { data: newCart, error } = await client
        .from("cart")
        .insert({ guest_session_id: guestSession.session_id })
        .select()
        .single();

      if (error) {
        console.error('Error creating guest cart:', error);
        throw error;
      }
      return newCart;
    },
  });

  const { data: items, isLoading: isLoadingItems } = useQuery<CartItem[]>({
    queryKey: ["cart-items", cart?.id],
    queryFn: async () => {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get appropriate client (with session token for guests)
      let client = supabase;
      if (!user) {
        const guestSession = await getGuestSession();
        if (guestSession) {
          client = getSupabaseClient(guestSession.session_token);
        }
      }
      
      // Fetch cart items with products and guest data from relational table
      const { data, error } = await client
        .from("cart_items")
        .select(`
          *,
          guests:cart_item_guests(*)
        `)
        .eq("cart_id", cart!.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as CartItem[];
    },
    enabled: !!cart?.id,
  });

  // Background validation function - validates ONLY the specific cart item independently
  const validateCartItem = async (cartItemId: string, { hotelId, roomId, searchToken, bookingDetails }: {
    hotelId: number;
    roomId: number;
    searchToken: string;
    bookingDetails: any;
  }) => {
    // Get appropriate client with session token for guests
    const { data: { user } } = await supabase.auth.getUser();
    let client = supabase;
    if (!user) {
      const guestSession = await getGuestSession();
      if (guestSession) {
        client = getSupabaseClient(guestSession.session_token);
      }
    }
    
    try {
      console.log(`[${cartItemId}] Starting independent validation:`, { hotelId, roomId, hasToken: !!searchToken });
      
      // Update status to validating for THIS specific item only
      await client
        .from('cart_items')
        .update({ validation_status: 'validating' })
        .eq('id', cartItemId);

      queryClient.invalidateQueries({ queryKey: ["cart-items"] });

      // SECURITY: Call server-side validation function that protects pricing formula
      console.log(`[${cartItemId}] Calling secure validate-cart-item function...`);
      
      const { data, error } = await client.functions.invoke('validate-cart-item', {
        body: {
          cartItemId,
          hotelId,
          roomId,
          searchToken,
          bookingDetails,
          locale: i18nInstance.language,
        },
      });

      console.log(`[${cartItemId}] Validation response:`, {
        data, 
        error,
        hasData: !!data,
        dataKeys: data ? Object.keys(data) : [],
        errorMessage: error?.message
      });

      if (error) throw error;

      // Server returns comparison results without exposing pricing calculations
      const changes = data.changes || [];
      const validatedData = data.validatedData;

      // Update THIS specific cart item with its independent validation result
      const updateData: any = {
        validated_at: new Date().toISOString(),
      };

      if (changes.length > 0) {
        console.log(`[${cartItemId}] Changes detected:`, changes);
        updateData.validation_status = 'changes_found';
        
        // Store changes along with validated data for updating when user confirms
        updateData.validation_error = JSON.stringify({
          changes,
          validatedData
        });
      } else if (data.validationStatus === 'validated' || data.validationStatus === 'changes_found') {
        console.log(`[${cartItemId}] Item validated successfully`);
        updateData.validation_status = 'validated';
        updateData.validation_error = null;
        
        // Update booking_details with validated data even if no changes detected
        // This ensures we have the latest information including MoreInformation
        if (validatedData?.booking_details) {
          updateData.booking_details = validatedData.booking_details;
        }
      } else if (data.validationStatus === 'not_available') {
        console.log(`[${cartItemId}] Item no longer available`);
        updateData.validation_status = 'not_available';
        updateData.validation_error = t('cart.notAvailable');
      }

      if (data.newToken) {
        updateData.search_token = data.newToken;
      }

      console.log(`[${cartItemId}] Updating cart item with validation result:`, updateData);

      await client
        .from('cart_items')
        .update(updateData)
        .eq('id', cartItemId);

      // Refresh cart items to show updated status
      queryClient.invalidateQueries({ queryKey: ["cart-items"] });

      // Handle validation results for THIS specific item
      if (updateData.validation_status === 'not_available') {
        console.log(`[${cartItemId}] Item not available, removing from cart`);
        toast({
          title: t('cart.roomNotAvailable'),
          description: t('cart.roomNotAvailableDescription'),
          variant: "destructive",
        });
        
        // Remove only THIS item from cart
        await client.from('cart_items').delete().eq('id', cartItemId);
        queryClient.invalidateQueries({ queryKey: ["cart-items"] });
      } else if (updateData.validation_status === 'changes_found') {
        console.log(`[${cartItemId}] Changes found in booking conditions - user will be prompted`);
        // Don't show toast here, the dialog will handle user interaction
      } else {
        console.log(`[${cartItemId}] Validation successful`);
      }
    } catch (error) {
      console.error(`[${cartItemId}] Error validating cart item:`, error);
      // Mark THIS specific item as validation error but don't remove from cart
      await client
        .from('cart_items')
        .update({
          validation_status: 'pending',
          validation_error: t('cart.validationError'),
        })
        .eq('id', cartItemId);
      
      queryClient.invalidateQueries({ queryKey: ["cart-items"] });
    }
  };

  const addItem = useMutation({
    mutationFn: async ({ 
      productId, 
      quantity = 1, 
      bookingDetails = {},
      searchToken
    }: { 
      productId: string; 
      quantity?: number; 
      bookingDetails?: any;
      searchToken?: string;
    }) => {
      if (!cart?.id) {
        throw new Error("Error al crear carrito");
      }

      // Get appropriate client with session token for guests
      const { data: { user } } = await supabase.auth.getUser();
      let supabaseClient = supabase;
      if (!user) {
        const guestSession = await getGuestSession();
        if (guestSession) {
          supabaseClient = getSupabaseClient(guestSession.session_token);
        }
      }
      
      const isHotelBooking = bookingDetails?.type === 'hotel' && bookingDetails?.hotelId && bookingDetails?.roomId;

      // Hotel products are created immediately before this mutation by the
      // backend function. Do not re-read the public product view here: the base
      // products table is intentionally restricted, and that extra read can fail
      // even though the product exists and the cart insert is valid.
      let product: { price_eur: number } | null = null;
      if (!isHotelBooking) {
        const { data, error } = await supabaseClient
          .from("products_public")
          .select("price_eur")
          .eq("id", productId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching product before cart insert:', error);
          throw new Error(t('cart.productNotFound'));
        }

        product = data;
        if (!product) throw new Error(t('cart.productNotFound'));
      }

      const existingItem = items?.find(item => item.product_id === productId);

      if (existingItem) {
        const { error } = await supabaseClient
          .from("cart_items")
          .update({ 
            quantity: existingItem.quantity + quantity,
            booking_details: { ...existingItem.booking_details, ...bookingDetails }
          })
          .eq("id", existingItem.id);

        if (error) throw error;
      } else {
        // Determine initial validation status based on product type
        const initialStatus = isHotelBooking ? 'pending' : 'validated';
        
        // For hotels, use the price from bookingDetails as it's already calculated correctly
        // For other products, use the price from the database
        const itemPrice = isHotelBooking && bookingDetails?.priceEur 
          ? bookingDetails.priceEur 
          : product!.price_eur;
        
        const { data: newItem, error } = await supabaseClient
          .from("cart_items")
          .insert({
            cart_id: cart.id,
            product_id: productId,
            quantity,
            price_eur: itemPrice,
            booking_details: bookingDetails,
            search_token: searchToken,
            validation_status: initialStatus,
          })
          .select()
          .single();

        if (error) throw error;

        console.log('New cart item added:', { 
          itemId: newItem.id,
          productType: bookingDetails?.type,
          isHotelBooking,
          initialStatus,
          hasSearchToken: !!searchToken,
          hotelId: bookingDetails?.hotelId,
          roomId: bookingDetails?.roomId,
          priceEur: itemPrice,
          priceSource: isHotelBooking && bookingDetails?.priceEur ? 'bookingDetails' : 'product'
        });

        // Start background validation ONLY for hotel bookings
        if (isHotelBooking) {
          if (searchToken) {
            console.log('Starting independent validation for hotel cart item:', newItem.id);
            // Validate this specific item independently in background
            // Convert roomId to number if it's a string
            const roomIdNumber = typeof bookingDetails.roomId === 'string' 
              ? parseInt(bookingDetails.roomId, 10) 
              : bookingDetails.roomId;
            
            validateCartItem(newItem.id, {
              hotelId: bookingDetails.hotelId,
              roomId: roomIdNumber,
              searchToken,
              bookingDetails,
            });
          } else {
            console.warn('Cannot validate hotel cart item - search token is missing:', newItem.id);
            // Mark as validated since we can't validate without token
            await supabase
              .from('cart_items')
              .update({
                validation_status: 'validated',
                validation_error: t('cart.cannotValidate'),
              })
              .eq('id', newItem.id);
          }
        } else {
          console.log('Non-hotel item added, no validation needed:', newItem.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-items"] });
      toast({
        title: t('cart.itemAdded'),
        description: t('cart.itemAddedDescription'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error'),
        description: error.message || t('cart.updateError'),
        variant: "destructive",
      });
    },
  });

  const updateQuantity = useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: string; quantity: number }) => {
      // Get appropriate client with session token for guests
      const { data: { user } } = await supabase.auth.getUser();
      let supabaseClient = supabase;
      if (!user) {
        const guestSession = await getGuestSession();
        if (guestSession) {
          supabaseClient = getSupabaseClient(guestSession.session_token);
        }
      }
      
      if (quantity < 1) {
        const { error } = await supabaseClient
          .from("cart_items")
          .delete()
          .eq("id", itemId);

        if (error) throw error;
      } else {
        const { error } = await supabaseClient
          .from("cart_items")
          .update({ quantity })
          .eq("id", itemId);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-items"] });
    },
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      // Get appropriate client with session token for guests
      const { data: { user } } = await supabase.auth.getUser();
      let supabaseClient = supabase;
      if (!user) {
        const guestSession = await getGuestSession();
        if (guestSession) {
          supabaseClient = getSupabaseClient(guestSession.session_token);
        }
      }
      
      const { error } = await supabaseClient
        .from("cart_items")
        .delete()
        .eq("id", itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-items"] });
      toast({
        title: t('cart.itemRemoved'),
        description: t('cart.itemRemovedDescription'),
      });
    },
  });

  const clearCart = useMutation({
    mutationFn: async () => {
      if (!cart?.id) return;

      // Get appropriate client with session token for guests
      const { data: { user } } = await supabase.auth.getUser();
      let supabaseClient = supabase;
      if (!user) {
        const guestSession = await getGuestSession();
        if (guestSession) {
          supabaseClient = getSupabaseClient(guestSession.session_token);
        }
      }
      
      const { error } = await supabaseClient
        .from("cart_items")
        .delete()
        .eq("cart_id", cart.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-items"] });
    },
  });

  const total = items?.reduce((sum, item) => sum + (item.price_eur * item.quantity), 0) || 0;
  const itemCount = items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const hasUnvalidatedItems = items?.some(item => 
    item.validation_status === 'pending' || 
    item.validation_status === 'validating' ||
    item.validation_status === 'changes_found'
  ) || false;

  // Function to manually revalidate an item (exported for manual trigger)
  const revalidateItem = async (itemId: string) => {
    const item = items?.find(i => i.id === itemId);
    if (!item) {
      toast({
        title: t('common.error'),
        description: t('cart.itemNotFound'),
        variant: "destructive",
      });
      return;
    }

    const details = item.booking_details as any;
    if (details?.type !== 'hotel' || !details?.hotelId || !details?.roomId) {
      toast({
        title: t('common.error'),
        description: t('cart.cannotValidateItem'),
        variant: "destructive",
      });
      return;
    }

    if (!item.search_token) {
      toast({
        title: t('cart.tokenNotAvailable'),
        description: t('cart.tokenNotAvailableDescription'),
        variant: "destructive",
      });
      return;
    }

    validateCartItem(itemId, {
      hotelId: details.hotelId,
      roomId: details.roomId,
      searchToken: item.search_token,
      bookingDetails: details,
    });

    toast({
      title: t('cart.validationStarted'),
      description: t('cart.validationStartedDescription'),
    });
  };

  return {
    items: items || [],
    isLoading: isLoadingCart || isLoadingItems,
    itemCount,
    total,
    addItem: addItem.mutateAsync,
    updateQuantity: updateQuantity.mutate,
    removeItem: removeItem.mutate,
    clearCart: clearCart.mutate,
    hasUnvalidatedItems,
    revalidateItem,
  };
};
